import React from "react"
import {
  compose,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from "recompact"
import { connect } from "react-redux"
import { Field, getFormValues, reduxForm, stopSubmit } from "redux-form"
import TagsInput from "src/components/TagsInput"
import get from "lodash/get"
import map from "ramda/es/map"
import entityApi from "src/hocs/entity"
import eventTypeModel from "src/models/eventType"
import ModalConfirm from "src/components/modals/ModalConfirm"
import Input from "src/components/Input"
import FieldSet from "@infotech/uikit-fieldset"
import Select from "src/components/Selects/Select"
import isEmpty from "lodash/isEmpty"
import allPlanTypes from "src/hocs/allPlanTypes"
import withQueryParams from "src/hocs/withQueryParams"
import PlanDurationPicker from "src/components/forms/planDurationPicker"
import { selectPlanTagsAvailable } from "src/engine/modules/features/selectors"
import { Box } from "@infotech/uikit-grid"
import { PrimaryButton, SecondaryButton } from "@infotech/uikit-button"
import queryString from "query-string"
import noop from "lodash/noop"
import { P } from "@infotech/uikit-text"
import moment from "moment-timezone"
import ExternalLink from "src/components/ExternalLink"
import makeCompareFunction from "src/helpers/makeCompareFunction"
import { DAY, MONTH, WEEK, YEAR } from "../../Calendar/constants"
import TemplatesPart from "./Templates"
import EventTypePart from "./EventType"
import styles from "./styles.css"

export const FORM_NAME = `createPlan`
export const getTestAttribute = name => `planner-plan-item-${name}`

const getCurrentTime = () =>
  moment()
    .set({
      h: 0,
      m: 0,
      s: 0,
      ms: 0,
    })
    .valueOf()

const HANDLE_DAY = 34560000

const selectValues = getFormValues(FORM_NAME)
const enhancer = compose(
  withQueryParams(),
  allPlanTypes(),
  withStateHandlers(
    () => ({
      isConfirmationModalOpen: false,
    }),
    {
      openConfirmationModal: () => () => ({
        isConfirmationModalOpen: true,
      }),
      closeConfirmationModal: () => () => ({
        isConfirmationModalOpen: false,
      }),
    },
  ),
  connect(
    state => ({
      id: get(selectValues(state), `eventTypeId`),
      planTypeId: get(selectValues(state), `planTypeId`),
      planName: get(selectValues(state), `name`),
      startDateTime: get(selectValues(state), `startDateTime`) || 0,
      endDateTime: get(selectValues(state), `endDateTime`) || 0,
      tags: get(selectValues(state), `tags`) || [],
      selectPlanTagsAvailable: selectPlanTagsAvailable(state),
    }),
    {
      setErrors: errors => stopSubmit(FORM_NAME, errors),
    },
  ),
  entityApi({
    to: `eventType`,
    model: eventTypeModel,
  }),
  withProps(props => ({
    isNew: props.match.path === `/plans/new/main`,
  })),
  withProps(({ dataForTabs }) => ({
    initialValues: get(dataForTabs, `plan.data`),
    plan: dataForTabs.plan,
  })),
  withPropsOnChange(
    ({ initialValues }, { initialValues: nextInitialValues }) => {
      const negate =
        !initialValues ||
        !initialValues.startDateTime ||
        !initialValues.endDateTime
      return initialValues !== nextInitialValues && negate
    },
    ({ initialValues }) => {
      /* We should define default date only on initial stage */
      if (
        !initialValues ||
        !initialValues.startDateTime ||
        !initialValues.endDateTime
      ) {
        return {
          initialValues: {
            ...initialValues,
            startDateTime: getCurrentTime(),
            endDateTime: getCurrentTime() + 86400000,
          },
        }
      }
      return {}
    },
  ),
  withPropsOnChange(
    makeCompareFunction([`plan.error`]),
    ({ plan: { error }, setErrors }) => {
      if (
        error &&
        error.data &&
        error.data.errorCode === `application.request.validation.failed`
      ) {
        const { exception, body } = error.data
        setErrors({
          ...map(array => array[0])(body),
          _error: exception,
        })
      }
    },
  ),
  reduxForm({
    enableReinitialize: true,
    validate: ({ startDateTime, endDateTime }) => {
      const errors = {}
      // Here we check situation: start > end when we use duration picker
      if (endDateTime === startDateTime - HANDLE_DAY) {
        errors.endDateTime = `Окончание плана должна быть заполнено`
      } else if (startDateTime >= endDateTime) {
        errors.startDateTime = `Время начала не должно быть больше времени конца`
      }
      return errors
    },
    form: FORM_NAME,
    onSubmit(
      data,
      dispatch,
      {
        plan: {
          api: { save },
          isVirtual,
        },
        workspace: { emit },
      },
    ) {
      emit(`save`)
      save({
        ...data,
        locked: isVirtual ? false : data.locked,
      })
    },
  }),
)

const getPlanTypeById = (eventTypes, id) => {
  if (!eventTypes || !id) {
    return undefined
  }
  return eventTypes.find(element => {
    return element.id === id
  })
}

const PlanTypeField = ({
  planTypes: { data },
  change,
  planName,
  planTypeId,
  startDateTime,
  isVirtual,
}) => (
  <div data-testid={getTestAttribute(`plan-type-id`)}>
    <Field
      component={Select}
      name="planTypeId"
      placeholder="Выберите тип плана"
      disabled={!isVirtual}
      options={data.map(item => ({
        value: item.id,
        label: item.name,
        color: item.color,
      }))}
      onChange={value => {
        const planType = getPlanTypeById(data, value)
        const planTypeName = planType.name
        const oldPlanType = getPlanTypeById(data, planTypeId)
        const oldPlanTypeName = oldPlanType && oldPlanType.name

        const { defaultPlanDurationValue } = planType
        const { defaultPlanDurationUnit } = planType

        let coefficient
        switch (defaultPlanDurationUnit) {
          case `YEAR`:
            coefficient = YEAR
            break
          case `MONTH`:
            coefficient = MONTH
            break
          case `WEEK`:
            coefficient = WEEK
            break
          case `DAY`:
            coefficient = DAY
            break
          default:
            coefficient = 0
        }
        if (
          planTypeName &&
          (isEmpty(planName) || oldPlanTypeName === planName)
        ) {
          change(`name`, planTypeName)
        }

        if (coefficient > 0 && isVirtual) {
          change(
            `endDateTime`,
            startDateTime + defaultPlanDurationValue * coefficient,
          )
        }
        change(`planTypeId`, value)
      }}
    />
  </div>
)

export default enhancer(props => {
  const {
    plan,
    handleSubmit,
    startDateTime,
    endDateTime,
    planName,
    planTypeId,
    tags,
    change,
    initialValues,
    dirty,
    history,
    queryParams,
    isConfirmationModalOpen,
    openConfirmationModal,
    closeConfirmationModal,
    planTypes,
    closeUrl,
    basicUrl,
    isNew,
    disableEditMain,
    disableEditEventTypes,
    disableEditReportTemplates,
    selectPlanTagsAvailable,
  } = props

  const planExternalLink = get(plan, `data.externalLink`)
  const isMainDisabledByPermission = disableEditMain && !isNew
  const isEventTypesDisabledByPermission = disableEditEventTypes && !isNew
  const isReportTemplatesDisabledByPermission =
    disableEditReportTemplates && !isNew
  const closeModal = () => {
    history.push(
      `${closeUrl || basicUrl}?${queryString.stringify(queryParams)}`,
    )
  }

  const submitButton = isMainDisabledByPermission ? null : (
    <Box loose mr="2md">
      <div data-testid={getTestAttribute(`button-save`)}>
        <PrimaryButton
          className={styles.Buttons}
          onClick={handleSubmit}
          disabled={!plan.isVirtual && !dirty && Boolean(initialValues)}
        >
          {plan.isVirtual ? `Создать` : `Сохранить`}
        </PrimaryButton>
      </div>
    </Box>
  )

  return (
    <Box flex className={styles.PlanItemWrapper}>
      <Box flex column className={styles.mainContentColumn}>
        <div className={styles.ContentWrapper}>
          <Box className={styles.ColumnContent}>
            <FieldSet caption="Тип плана" required>
              <PlanTypeField
                change={change}
                planName={planName}
                planTypeId={planTypeId}
                startDateTime={startDateTime}
                isVirtual={plan.isVirtual}
                planTypes={planTypes}
              />
            </FieldSet>
            <FieldSet caption="Наименование плана" required>
              <div data-testid={getTestAttribute(`name`)}>
                <Field
                  component={Input}
                  name="name"
                  props={{
                    disabled: isMainDisabledByPermission,
                  }}
                />
              </div>
            </FieldSet>
            <PlanDurationPicker
              startDateTime={startDateTime}
              endDateTime={endDateTime}
              change={change}
              pending={plan.pending}
              isDisabled={isMainDisabledByPermission}
              isNew={isNew}
            />
            {selectPlanTagsAvailable && (
              <FieldSet caption="Список тегов">
                <div data-testid={getTestAttribute(`tags`)}>
                  <Field
                    name="tags"
                    component={TagsInput}
                    props={{
                      tagsArray: tags,
                      disabled: isMainDisabledByPermission,
                    }}
                  />
                </div>
              </FieldSet>
            )}
            {!isNew && planExternalLink && (
              <ExternalLink data={planExternalLink} />
            )}
          </Box>
        </div>
        <Box loose flex className={styles.buttonWrapper} p="2md">
          {submitButton}
          <Box loose onClick={dirty ? openConfirmationModal : closeModal}>
            <div data-testid={getTestAttribute(`button-cancel`)}>
              <SecondaryButton className={styles.Buttons}>
                Отмена
              </SecondaryButton>
            </div>
          </Box>
        </Box>
      </Box>
      <Box className={styles.mainContentColumn}>
        <EventTypePart
          plan={plan}
          planTypeId={planTypeId}
          isDisabled={isEventTypesDisabledByPermission}
        />
      </Box>
      <Box className={styles.mainContentColumn}>
        <TemplatesPart
          plan={plan}
          planTypeId={planTypeId}
          planTypes={planTypes}
          isDisabled={isReportTemplatesDisabledByPermission}
        />
      </Box>
      <ModalConfirm
        isOpen={isConfirmationModalOpen}
        title="Закрыть без сохранения?"
        buttonConf={[
          {
            text: `Закрыть`,
            onClick: dirty ? closeModal : noop,
            isOk: true,
            done: true,
            disabled: false,
          },
          {
            text: `Отмена`,
            onClick: closeConfirmationModal,
            disabled: false,
          },
        ]}
        isClose
      >
        <div className={styles[`modal-message-wrapper`]}>
          <P className={styles[`modal-message-text`]}>
            Все сделанные вами изменения будут утеряны
          </P>
        </div>
      </ModalConfirm>
    </Box>
  )
})
